import React from 'react';
import { Plus, Image, Eye, EyeOff, Edit, Trash2 } from 'lucide-react';
import SafeImage from '../../SafeImage';
import { API_BASE_URL } from '../../../api';

const HeroSlidesTab = ({ heroSlides: heroSlidesHook }) => {
  const {
    heroSlides, loadingHeroSlides, heroSlideStats, openAddModal, openEditModal,
    handleToggleHeroSlide, handleDeleteHeroSlide,
  } = heroSlidesHook;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Hero Slides Management</h2>
          <p className="text-sm text-gray-600 mt-1">Manage homepage hero slider content, images, and actions</p>
        </div>
        <button onClick={openAddModal} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Add Hero Slide
        </button>
      </div>

      {/* Hero Slides Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Slides</p>
              <p className="text-2xl font-bold text-blue-600">{heroSlideStats.total}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <Image className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Slides</p>
              <p className="text-2xl font-bold text-green-600">{heroSlideStats.active}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <Eye className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Inactive Slides</p>
              <p className="text-2xl font-bold text-gray-600">{heroSlideStats.inactive}</p>
            </div>
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
              <EyeOff className="h-6 w-6 text-gray-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Hero Slides Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800">Hero Slides</h3>
        </div>

        {loadingHeroSlides ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading hero slides...</p>
          </div>
        ) : heroSlides.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Image className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>No hero slides found</p>
            <button onClick={openAddModal} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
              Create First Hero Slide
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Preview</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Badge</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {heroSlides.map((slide) => (
                  <tr key={slide.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="w-16 h-10 rounded-lg overflow-hidden bg-gray-200">
                        <SafeImage
                          src={`${API_BASE_URL}${slide.image}`}
                          alt={slide.title}
                          className="w-full h-full object-cover"
                          fallback={<div className="w-full h-full bg-gray-300 flex items-center justify-center text-gray-500 text-xs">No Image</div>}
                        />
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{slide.title}</div>
                        <div className="text-sm text-gray-500">{slide.subtitle}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {slide.badge && (
                        <span
                          className="inline-flex px-2 py-1 text-xs font-semibold rounded-full"
                          style={{ backgroundColor: `${slide.accent}20`, color: slide.accent || '#374151' }}
                        >
                          {slide.badge}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{slide.order}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${slide.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                        {slide.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button onClick={() => openEditModal(slide)} className="text-blue-600 hover:text-blue-900 p-1 rounded" title="Edit slide">
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleToggleHeroSlide(slide.id, slide.isActive)}
                          className={`p-1 rounded ${slide.isActive ? 'text-orange-600 hover:text-orange-900' : 'text-green-600 hover:text-green-900'}`}
                          title={slide.isActive ? 'Deactivate slide' : 'Activate slide'}
                        >
                          {slide.isActive ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                        <button onClick={() => handleDeleteHeroSlide(slide.id, slide.title)} className="text-red-600 hover:text-red-900 p-1 rounded" title="Delete slide">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default HeroSlidesTab;
